/**
 * SubstitutionGrid — Visual coverage board for a single date.
 *
 * v2:
 * - New Assign Modal: static subject/period info + Quick Suggestions ranked cards
 * - Quick suggestion cards pre-fill the TeacherSelector; admin still hits Assign
 * - Per-row progress animates during auto-assign
 * - Any cell click (assigned or unassigned) opens the modal
 * - Teacher name/avatar click opens DraftReviewPanel drawer
 * - onStatsChange callback lifts pending/covered/published counts to parent
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Button,
  Avatar,
  LinearProgress,
  Drawer,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  styled,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getDraftAssignments,
  updateAssignment,
  createAssignment,
  removeAssignment,
  discardDraft,
  triggerEngine,
  rerunAutoAssignment,
  getClassSchedulesDirect,
  getTeachersDirect,
  getTimetableRecordsDirect,
  recallAndRedraft,
  getByTeacherDirect,
} from '@timetable/api';
import type { DraftAssignment, AbsenceWithStatus, SchedulePeriod, Teacher, AutoAssignProgress } from '@timetable/types';
import { useAuthStore } from '@/store/authStore';
import { useToast } from '@/components/Toast';
import { queryKeys } from '@/lib/queryKeys';
import { TeacherSelector } from '@/components/absences/TeacherSelector';
import { DraftReviewPanel } from '@/components/absences/DraftReviewPanel';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { AutoAssignProgressDialog } from './AutoAssignProgressDialog';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GridStats {
  pending: number;
  covered: number;
  published: number;
}

export interface SubstitutionGridProps {
  date: string;
  absences: AbsenceWithStatus[];
  absentTeacherIds: Set<string>;
  onStatsChange?: (stats: GridStats) => void;
}

interface AssignModalState {
  assignment: DraftAssignment;
  /** Pre-filled teacher ID from a quick suggestion click */
  prefillTeacherId?: string | null;
  /** Conflict details to show in the modal banner */
  conflictInfo?: {
    type: 'scheduling' | 'absent' | 'inactive';
    label: string;
  };
  /** Whether the engine tried and failed to find a teacher */
  cantAutoAssign?: boolean;
  /** Callback to remove the assignment (shown as destructive action in modal) */
  onRemove?: () => void;
}

/**
 * Lightweight context for a period that has no DB assignment record yet (Case 2).
 * Used to open the assign modal before the engine has run.
 */
interface UnassignedPeriodContext {
  absenceId: string;
  absentTeacherId: string;
  schoolId: string;
  date: string;
  periodId: string;
  classSectionId: string;
  subjectId: string;
  className: string;
  subjectName: string;
  periodNumber: number;
  timeStart: string;
  timeEnd: string;
}

interface DrawerState {
  absenceId: string;
  teacherName: string;
  absenceType: 'full_day' | 'temporary_duty';
  absenceStatus: string;
}

interface ConfirmState {
  open: boolean;
  title: string;
  message: string;
  onConfirm: () => void;
}

// ─── useBusyTeacherIds ────────────────────────────────────────────────────────

/**
 * Fetches all timetable records for the school on the given day and returns
 * the set of teacher IDs who have a class that overlaps with the given time slot.
 * Uses time-overlap (not period_id equality) so cross-schedule conflicts are caught.
 */
function useBusyTeacherIds(
  schoolId: string | null,
  dayOfWeek: string,
  timeStart: string,
  timeEnd: string,
): Set<string> {
  const { data: records = [] } = useQuery({
    queryKey: [...queryKeys.timetable.all, 'busy-teachers', schoolId, dayOfWeek],
    queryFn: () => getTimetableRecordsDirect({ school_id: schoolId!, day_of_week: dayOfWeek as 'MONDAY' | 'TUESDAY' | 'WEDNESDAY' | 'THURSDAY' | 'FRIDAY' | 'SATURDAY' | 'SUNDAY' }),
    enabled: !!schoolId && !!dayOfWeek,
    staleTime: 30_000,
  });

  return useMemo(() => {
    const busy = new Set<string>();
    for (const r of records) {
      const rStart = r.period?.time_start;
      const rEnd = r.period?.time_end;
      if (!rStart || !rEnd) continue;
      // Overlap: record starts before our slot ends AND ends after our slot starts
      if (rStart < timeEnd && rEnd > timeStart) {
        busy.add(r.teacher_id);
      }
    }
    return busy;
  }, [records, timeStart, timeEnd]);
}

// ─── Quick Suggestion Card ────────────────────────────────────────────────────

interface SuggestionCardProps {
  teacher: Teacher;
  dailyCount: number;
  subjectMatch: boolean;
  isBusy: boolean;
  isSelected: boolean;
  onClick: () => void;
}

function SuggestionCard({ teacher, dailyCount, subjectMatch, isBusy, isSelected, onClick }: SuggestionCardProps) {
  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 1.5,
        cursor: 'pointer',
        borderColor: isSelected && !isBusy ? 'primary.main' : 'divider',
        bgcolor: isBusy ? 'grey.50' : isSelected ? 'primary.light' : 'background.paper',
        borderWidth: isSelected && !isBusy ? 2 : 1,
        opacity: isBusy ? 0.6 : 1,
        transition: 'all 0.15s',
        '&:hover': { borderColor: isBusy ? 'divider' : 'primary.main', boxShadow: isBusy ? 0 : 1 },
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minWidth: 180,
        flexShrink: 0,
      }}
    >
      <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: isBusy ? 'grey.400' : isSelected ? 'primary.main' : 'primary.light' }}>
        {teacher.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()}
      </Avatar>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" fontWeight="medium" noWrap color={isBusy ? 'text.secondary' : 'text.primary'}>{teacher.name}</Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', mt: 0.25 }}>
          {isBusy && (
            <Chip label="Busy" size="small" color="error" variant="outlined"
              sx={{ height: 18, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }} />
          )}
          {subjectMatch && !isBusy && (
            <Chip label="Subject match" size="small" color="success" variant="outlined"
              sx={{ height: 18, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }} />
          )}
          <Typography variant="caption" color="text.secondary">{dailyCount} today</Typography>
        </Box>
      </Box>
    </Paper>
  );
}

// ─── Assign Modal ─────────────────────────────────────────────────────────────

interface AssignModalProps {
  state: AssignModalState;
  absentTeacherIds: Set<string>;
  onClose: () => void;
  onAssign: (teacherId: string) => void;
  isPending: boolean;
}

function AssignModal({ state, absentTeacherIds, onClose, onAssign, isPending }: AssignModalProps) {
  const { schoolContext } = useAuthStore();
  const { assignment, prefillTeacherId, conflictInfo, cantAutoAssign, onRemove } = state;
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(
    prefillTeacherId ?? assignment.substitute_teacher_id
  );

  const { data: teachers = [] } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '' }),
    queryFn: () => getTeachersDirect({ school_id: schoolContext || undefined }),
    enabled: !!schoolContext,
  });

  // Derive day of week from the assignment date
  const dayOfWeek = useMemo(() => {
    const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    const [y, m, d] = assignment.date.split('-').map(Number);
    return days[new Date(y, m - 1, d).getDay()];
  }, [assignment.date]);

  // Fetch teachers busy at this time slot via timetable time-overlap check
  const busyTeacherIds = useBusyTeacherIds(
    schoolContext,
    dayOfWeek,
    assignment.period.time_start,
    assignment.period.time_end,
  );

  const suggestions = useMemo(() => {
    const subjectId = assignment.subject?.id;
    return (teachers as any[])
      .filter((t) => t.is_active && !absentTeacherIds.has(t.id) && t.id !== assignment.absent_teacher_id)
      .map((t) => ({
        teacher: t as Teacher,
        subjectMatch: !!(subjectId && Array.isArray(t.subjects) && t.subjects.some((s: any) => s.id === subjectId)),
        dailyCount: 0,
        isBusy: busyTeacherIds.has(t.id),
      }))
      .sort((a, b) => {
        // Busy teachers go last
        if (a.isBusy !== b.isBusy) return a.isBusy ? 1 : -1;
        if (a.subjectMatch !== b.subjectMatch) return a.subjectMatch ? -1 : 1;
        return a.teacher.name.localeCompare(b.teacher.name);
      })
      .slice(0, 5);
  }, [teachers, absentTeacherIds, assignment, busyTeacherIds]);

  const isEdit = !!assignment.substitute_teacher_id;
  const hasConflict = !!conflictInfo;

  // True when the currently selected teacher is busy at this time slot per the timetable
  const selectedTeacherIsBusy = !!selectedTeacherId && busyTeacherIds.has(selectedTeacherId);

  // Derive the busy teacher's name for the conflict message
  const selectedTeacherName = useMemo(
    () => (teachers as any[]).find((t) => t.id === selectedTeacherId)?.name ?? 'This teacher',
    [teachers, selectedTeacherId]
  );

  // Title: conflict → "Fix Conflict", edit → "Change Substitute", new → "Assign Substitute"
  const modalTitle = hasConflict ? 'Fix Conflict' : isEdit ? 'Change Substitute' : 'Assign Substitute';

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="medium">{modalTitle}</Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {/* Period + class info */}
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <SchoolIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Period {assignment.period.period_number} · {assignment.period.time_start.slice(0, 5)}–{assignment.period.time_end.slice(0, 5)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {assignment.class_section.class.class_name}{assignment.class_section.section}
              </Typography>
            </Box>
            <Chip label={assignment.subject.subject_name} size="small" variant="outlined" sx={{ height: 22 }} />
          </Box>
        </Paper>

        {/* Conflict banner — shown when substitute has a conflict */}
        {conflictInfo && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#FEF2F2', borderColor: '#FECACA' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 18, color: '#991B1B', mt: 0.1, flexShrink: 0 }} />
              <Box>
                <Typography variant="body2" fontWeight="medium" sx={{ color: '#991B1B', lineHeight: 1.4 }}>
                  {conflictInfo.label}
                </Typography>
                <Typography variant="caption" sx={{ color: '#B91C1C', lineHeight: 1.4 }}>
                  Select a different teacher below to resolve this conflict.
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Can't auto-assign banner — shown when engine tried and failed */}
        {cantAutoAssign && !conflictInfo && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#FFFBEB', borderColor: '#FDE68A' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 18, color: '#92400E', mt: 0.1, flexShrink: 0 }} />
              <Box>
                <Typography variant="body2" fontWeight="medium" sx={{ color: '#92400E', lineHeight: 1.4 }}>
                  Auto-assign couldn't find an eligible teacher
                </Typography>
                <Typography variant="caption" sx={{ color: '#B45309', lineHeight: 1.4 }}>
                  All available teachers may be busy, at quota, or unavailable for this period. You can still assign manually below.
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Timetable conflict banner — shown when selected teacher is already teaching at this time */}
        {selectedTeacherIsBusy && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#FEF2F2', borderColor: '#FECACA' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 18, color: '#991B1B', mt: 0.1, flexShrink: 0 }} />
              <Box>
                <Typography variant="body2" fontWeight="medium" sx={{ color: '#991B1B', lineHeight: 1.4 }}>
                  {selectedTeacherName} is already teaching another class at this time
                </Typography>
                <Typography variant="caption" sx={{ color: '#B91C1C', lineHeight: 1.4 }}>
                  Select a different teacher to avoid a scheduling conflict.
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Quick suggestions */}
        {suggestions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="medium" sx={{ mb: 1, display: 'block' }}>
              Quick suggestions · Ranked by availability &amp; subject match
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
              {suggestions.map(({ teacher, subjectMatch, dailyCount, isBusy }) => (
                <SuggestionCard
                  key={teacher.id}
                  teacher={teacher}
                  subjectMatch={subjectMatch}
                  dailyCount={dailyCount}
                  isBusy={isBusy}
                  isSelected={selectedTeacherId === teacher.id}
                  onClick={() => setSelectedTeacherId(teacher.id)}
                />
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <TeacherSelector
          value={selectedTeacherId}
          onChange={setSelectedTeacherId}
          targetDate={assignment.date}
          periodId={assignment.period_id}
          absentTeacherIds={[...absentTeacherIds]}
          busyTeacherIds={[...busyTeacherIds]}
          size="medium"
          label="Select substitute teacher"
          placeholder="Search by name…"
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'space-between' }}>
        {/* Remove assignment — destructive, left-aligned, only for draft assignments */}
        {onRemove && assignment.status === 'draft' ? (
          <Button
            variant="text"
            color="error"
            onClick={() => { onRemove(); onClose(); }}
            startIcon={<DeleteOutlineIcon />}
          >
            Remove
          </Button>
        ) : (
          <Box />
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!selectedTeacherId || isPending || selectedTeacherIsBusy}
            onClick={() => { if (selectedTeacherId) onAssign(selectedTeacherId); }}
            startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {hasConflict ? 'Reassign' : isEdit ? 'Change' : 'Assign'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

// ─── NewAssignModal — for Case 2 (no DB record yet) ──────────────────────────

interface NewAssignModalProps {
  ctx: UnassignedPeriodContext;
  absentTeacherIds: Set<string>;
  onClose: () => void;
  onAssign: (teacherId: string) => void;
  isPending: boolean;
}

function NewAssignModal({ ctx, absentTeacherIds, onClose, onAssign, isPending }: NewAssignModalProps) {
  const { schoolContext } = useAuthStore();
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);

  const { data: teachers = [] } = useQuery({
    queryKey: queryKeys.teachers.list({ school_id: schoolContext || '' }),
    queryFn: () => getTeachersDirect({ school_id: schoolContext || undefined }),
    enabled: !!schoolContext,
  });

  // Derive day of week from the context date
  const dayOfWeek = useMemo(() => {
    const days = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];
    const [y, m, d] = ctx.date.split('-').map(Number);
    return days[new Date(y, m - 1, d).getDay()];
  }, [ctx.date]);

  // Fetch teachers busy at this time slot via timetable time-overlap check
  const busyTeacherIds = useBusyTeacherIds(
    schoolContext,
    dayOfWeek,
    ctx.timeStart,
    ctx.timeEnd,
  );

  const suggestions = useMemo(() => {
    return (teachers as any[])
      .filter((t) => t.is_active && !absentTeacherIds.has(t.id) && t.id !== ctx.absentTeacherId)
      .map((t) => ({
        teacher: t as Teacher,
        subjectMatch: !!(ctx.subjectId && Array.isArray(t.subjects) && t.subjects.some((s: any) => s.id === ctx.subjectId)),
        dailyCount: 0,
        isBusy: busyTeacherIds.has(t.id),
      }))
      .sort((a, b) => {
        if (a.isBusy !== b.isBusy) return a.isBusy ? 1 : -1;
        if (a.subjectMatch !== b.subjectMatch) return a.subjectMatch ? -1 : 1;
        return a.teacher.name.localeCompare(b.teacher.name);
      })
      .slice(0, 5);
  }, [teachers, absentTeacherIds, ctx, busyTeacherIds]);

  // True when the currently selected teacher is busy at this time slot per the timetable
  const selectedTeacherIsBusy = !!selectedTeacherId && busyTeacherIds.has(selectedTeacherId);

  const selectedTeacherName = useMemo(
    () => (teachers as any[]).find((t) => t.id === selectedTeacherId)?.name ?? 'This teacher',
    [teachers, selectedTeacherId]
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight="medium">Assign Substitute</Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <SchoolIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                Period {ctx.periodNumber} · {ctx.timeStart.slice(0, 5)}–{ctx.timeEnd.slice(0, 5)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">{ctx.className}</Typography>
            </Box>
            <Chip label={ctx.subjectName} size="small" variant="outlined" sx={{ height: 22 }} />
          </Box>
        </Paper>

        {/* Timetable conflict banner — shown when selected teacher is already teaching at this time */}
        {selectedTeacherIsBusy && (
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#FEF2F2', borderColor: '#FECACA' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <WarningAmberIcon sx={{ fontSize: 18, color: '#991B1B', mt: 0.1, flexShrink: 0 }} />
              <Box>
                <Typography variant="body2" fontWeight="medium" sx={{ color: '#991B1B', lineHeight: 1.4 }}>
                  {selectedTeacherName} is already teaching another class at this time
                </Typography>
                <Typography variant="caption" sx={{ color: '#B91C1C', lineHeight: 1.4 }}>
                  Select a different teacher to avoid a scheduling conflict.
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}

        {suggestions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" fontWeight="medium" sx={{ mb: 1, display: 'block' }}>
              Quick suggestions · Ranked by availability &amp; subject match
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
              {suggestions.map(({ teacher, subjectMatch, dailyCount, isBusy }) => (
                <SuggestionCard
                  key={teacher.id}
                  teacher={teacher}
                  subjectMatch={subjectMatch}
                  dailyCount={dailyCount}
                  isBusy={isBusy}
                  isSelected={selectedTeacherId === teacher.id}
                  onClick={() => setSelectedTeacherId(teacher.id)}
                />
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        <TeacherSelector
          value={selectedTeacherId}
          onChange={setSelectedTeacherId}
          targetDate={ctx.date}
          periodId={ctx.periodId}
          absentTeacherIds={[...absentTeacherIds]}
          busyTeacherIds={[...busyTeacherIds]}
          size="medium"
          label="Select substitute teacher"
          placeholder="Search by name…"
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="outlined" onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!selectedTeacherId || isPending || selectedTeacherIsBusy}
          onClick={() => { if (selectedTeacherId) onAssign(selectedTeacherId); }}
          startIcon={isPending ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Assign
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Styled cell container (mirrors timetable PeriodCard anatomy) ─────────────

const CellContainer = styled(Box, {
  shouldForwardProp: (p) => !['accentColor', 'isClickable', 'isDashed'].includes(p as string),
})<{ accentColor?: string; isClickable?: boolean; isDashed?: boolean }>(({ theme, accentColor, isClickable, isDashed }) => ({
  width: 110,
  height: '100%',
  flexShrink: 0,
  borderRadius: 8,
  backgroundColor: theme.palette.background.paper,
  // Full border — dashed for incomplete, solid for complete
  border: `2px ${isDashed ? 'dashed' : 'solid'} ${accentColor || theme.palette.divider}`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  overflow: 'hidden',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: isClickable ? 'pointer' : 'default',
  position: 'relative',
  ...(isClickable && {
    '&:hover': {
      boxShadow: theme.shadows[2],
      transform: 'translateY(-1px)',
    },
  }),
}));

// ─── Cell ─────────────────────────────────────────────────────────────────────

interface TimetablePeriodInfo {
  classSectionId: string;
  subjectId: string;
  className: string;   // e.g. "Class X"
  section: string;     // e.g. "A"
  subjectName: string;
}

interface CellProps {
  assignment: DraftAssignment | undefined;
  period: SchedulePeriod;
  needsSubstitution: boolean;
  /** Timetable info for this period — only present when needsSubstitution=true and no assignment */
  timetableInfo?: TimetablePeriodInfo;
  absentTeacherIds: Set<string>;
  /** True when this assignment's substitute is already assigned to another class in the same period */
  isSchedulingConflict?: boolean;
  isAdmin: boolean;
  onClick: (periodId: string, existingAssignment?: DraftAssignment) => void;
  onClickUnassigned: (periodId: string, info: TimetablePeriodInfo) => void;
}

function GridCell({
  assignment,
  period,
  needsSubstitution,
  timetableInfo,
  absentTeacherIds,
  isSchedulingConflict = false,
  isAdmin,
  onClick,
  onClickUnassigned,
}: CellProps) {
  // ── Case 1: Genuinely free — teacher had no class here ──────────────────
  if (!assignment && !needsSubstitution) {
    return (
      <Box sx={{
        width: 110, height: '100%', flexShrink: 0, borderRadius: '6px',
        bgcolor: 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1 }}>No class</Typography>
      </Box>
    );
  }

  // ── Case 2: Needs substitution — teacher had class, no assignment yet ───
  if (!assignment && needsSubstitution) {
    const displayClass = timetableInfo
      ? `${timetableInfo.className}${timetableInfo.section}`
      : '—';
    const displaySubject = timetableInfo?.subjectName ?? '—';

    // Neutral tooltip — no warning, just status + CTA
    const tooltipContent = (
      <Box sx={{ p: 0.5 }}>
        <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', lineHeight: 1.3 }}>
          No substitute assigned yet
        </Typography>
        {isAdmin && (
          <Typography sx={{ fontSize: 11, color: '#E2E8F0', fontWeight: 600, lineHeight: 1.3, mt: 0.5 }}>
            Click to assign →
          </Typography>
        )}
      </Box>
    );

    // Neutral slate color — no urgency, just pending
    const neutralAccent = '#64748B'; // slate-500
    const neutralBg = '#F8FAFC';     // slate-50

    return (
      <Tooltip
        title={tooltipContent}
        placement="top"
        arrow
        componentsProps={{
          tooltip: { sx: { bgcolor: '#475569', maxWidth: 220, '& .MuiTooltip-arrow': { color: '#475569' } } },
        }}
      >
        <CellContainer
          accentColor="#94A3B8"
          isDashed
          isClickable={isAdmin}
          onClick={() => isAdmin && timetableInfo ? onClickUnassigned(period.id, timetableInfo) : undefined}
          sx={{ bgcolor: '#F8FAFC', width: 110 }}
        >
          {/* Zone 1 — class + subject */}
          <Box sx={{ px: 1.25, pt: 0.75, pb: 0.25 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }} noWrap>
              {displayClass}
            </Typography>
            <Typography sx={{ fontSize: 10, lineHeight: 1.2, color: 'text.secondary', mt: 0.25 }} noWrap>
              {displaySubject}
            </Typography>
          </Box>
          {/* Zone 2 — neutral status dot */}
          <Box sx={{ px: 1.25, pb: 0.75 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#94A3B8' }} />
          </Box>
        </CellContainer>
      </Tooltip>
    );
  }

  // ── Case 3: Assignment exists ────────────────────────────────────────────
  const isPosted = assignment!.status === 'posted';
  const isDraft = assignment!.status === 'draft';
  const isUnassigned = assignment!.substitute_teacher_id === null;
  const subIsAbsent = !!assignment!.substitute_teacher_id && absentTeacherIds.has(assignment!.substitute_teacher_id);
  const subIsInactive = assignment!.substitute_teacher != null && !assignment!.substitute_teacher.is_active;
  const hasConflict = subIsAbsent || subIsInactive || isSchedulingConflict;

  // Accent color + background
  let accentColor = '#92400E';
  let cellBg = '#FFFBEB';
  if (hasConflict)                    { accentColor = '#DC2626'; cellBg = '#FEF2F2'; }
  else if (isPosted)                  { accentColor = '#16A34A'; cellBg = '#F0FDF4'; }
  else if (isDraft && !isUnassigned)  { accentColor = '#2563EB'; cellBg = '#EFF6FF'; } // blue — distinct from header teal
  else if (isUnassigned)              { accentColor = '#D97706'; cellBg = '#FEF3C7'; } // amber-600 / amber-100

  const isClickable = isAdmin && !isPosted;
  // Dashed border = incomplete (no teacher assigned yet), solid = complete
  const isDashed = isUnassigned;

  const displayClass = `${assignment!.class_section.class.class_name}${assignment!.class_section.section}`;
  const displaySubject = assignment!.subject.subject_name;
  const teacherName = assignment!.substitute_teacher?.name ?? null;

  // ── Tooltip content — color-coded per state ──────────────────────────────
  let tooltipBg = '#0F766E';
  let tooltipContent: React.ReactNode;

  if (isUnassigned) {
    const cantAutoAssign = assignment!.unassigned_reason === 'no_eligible_teachers';
    tooltipBg = '#92400E';
    tooltipContent = (
      <Box sx={{ p: 0.5 }}>
        {/* Warning is primary when auto-assign failed */}
        {cantAutoAssign ? (
          <>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#FDE68A', lineHeight: 1.3 }}>
              Auto-assign failed
            </Typography>
            <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.85)', lineHeight: 1.3, mt: 0.5 }}>
              No eligible teacher found for this period
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', lineHeight: 1.3 }}>
            No substitute assigned
          </Typography>
        )}
        {isAdmin && (
          <Typography sx={{ fontSize: 11, color: '#FDE68A', fontWeight: 600, lineHeight: 1.3, mt: 0.5 }}>
            Click to assign →
          </Typography>
        )}
      </Box>
    );
  } else if (hasConflict) {
    const conflictLabel = isSchedulingConflict
      ? 'Assigned to another class at this time'
      : subIsAbsent
        ? 'Teacher is marked absent today'
        : 'Teacher is currently inactive';
    tooltipBg = '#DC2626';
    tooltipContent = (
      <Box sx={{ p: 0.5 }}>
        {/* Conflict reason is primary — white for max contrast on red */}
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
          ⚠ {conflictLabel}
        </Typography>
        {teacherName && (
          <Typography sx={{ fontSize: 11, color: '#FECACA', lineHeight: 1.3, mt: 0.5 }}>
            Assigned: {teacherName}
          </Typography>
        )}
        {isAdmin && (
          <Typography sx={{ fontSize: 11, color: '#fff', fontWeight: 600, lineHeight: 1.3, mt: 0.5 }}>
            Click to reassign →
          </Typography>
        )}
      </Box>
    );
  } else if (isPosted) {
    tooltipBg = '#15803D';
    tooltipContent = (
      <Box sx={{ p: 0.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
          {displayClass} · {displaySubject}
        </Typography>
        {teacherName && (
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.9)', lineHeight: 1.3, mt: 0.5 }}>
            {teacherName}
          </Typography>
        )}
        <Typography sx={{ fontSize: 11, color: '#86EFAC', lineHeight: 1.3, mt: 0.5 }}>
          ✓ Published
        </Typography>
      </Box>
    );
  } else {
    // Draft, valid — blue tooltip
    tooltipBg = '#1D4ED8';
    tooltipContent = (
      <Box sx={{ p: 0.5 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>
          {displayClass} · {displaySubject}
        </Typography>
        {teacherName && (
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.9)', lineHeight: 1.3, mt: 0.5 }}>
            {teacherName}
          </Typography>
        )}
        <Typography sx={{ fontSize: 11, color: '#BFDBFE', lineHeight: 1.3, mt: 0.5 }}>
          ● Draft
        </Typography>
        {isAdmin && (
          <Typography sx={{ fontSize: 11, color: '#BFDBFE', fontWeight: 600, lineHeight: 1.3, mt: 0.5 }}>
            Click to change →
          </Typography>
        )}
      </Box>
    );
  }

  // ── Zone 2 — compact, icon-only for conflicts ────────────────────────────
  let zone2: React.ReactNode;

  if (isUnassigned) {
    const cantAutoAssign = assignment!.unassigned_reason === 'no_eligible_teachers';
    zone2 = (
      <Box sx={{ px: 1.25, pb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {cantAutoAssign
          ? <PersonSearchIcon sx={{ fontSize: 12, color: '#D97706' }} />
          : <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#D97706', opacity: 0.6 }} />
        }
      </Box>
    );
  } else if (hasConflict) {
    // WarningAmberIcon for conflict — distinct from PersonSearchIcon for auto-fail
    zone2 = (
      <Box sx={{ px: 1.25, pb: 0.75 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2, color: 'text.primary' }} noWrap>
          {teacherName ?? '—'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <WarningAmberIcon sx={{ fontSize: 11, color: '#DC2626' }} />
        </Box>
      </Box>
    );
  } else {
    // Valid assignment — teacher name + status dot/icon
    zone2 = (
      <Box sx={{ px: 1.25, pb: 0.75 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, lineHeight: 1.2, color: 'text.primary' }} noWrap>
          {teacherName ?? '—'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          {isPosted
            ? <CheckCircleIcon sx={{ fontSize: 10, color: '#16A34A' }} />
            : <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#2563EB', flexShrink: 0 }} />
          }
        </Box>
      </Box>
    );
  }

  return (
    <Tooltip
      title={tooltipContent}
      placement="top"
      arrow
      componentsProps={{
        tooltip: { sx: { bgcolor: tooltipBg, maxWidth: 240, '& .MuiTooltip-arrow': { color: tooltipBg } } },
      }}
    >
      <CellContainer
        accentColor={accentColor}
        isClickable={isClickable}
        isDashed={isDashed}
        onClick={() => isClickable ? onClick(period.id, assignment!) : undefined}
        sx={{ bgcolor: cellBg, width: 110 }}
      >
        {/* Zone 1 — class + subject, always top */}
        <Box sx={{ px: 1.25, pt: 0.75, pb: 0.25 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1.2, color: 'text.primary' }} noWrap>
            {displayClass}
          </Typography>
          <Typography sx={{ fontSize: 10, lineHeight: 1.2, color: 'text.secondary', mt: 0.25 }} noWrap>
            {displaySubject}
          </Typography>
        </Box>
        {/* Zone 2 — compact status */}
        {zone2}
      </CellContainer>
    </Tooltip>
  );
}

// ─── TeacherInfoCell — left frozen panel cell ─────────────────────────────────

interface TeacherInfoCellProps {
  absence: AbsenceWithStatus;
  date: string;
  rowBg: string;
  rowHeight: number;
  onOpenDetail: (state: DrawerState) => void;
  isAutoAssigning: boolean;
  onStatsUpdate: (absenceId: string, pending: number, covered: number, published: number) => void;
  onAssignmentsLoaded: (absenceId: string, assignments: DraftAssignment[]) => void;
}

function TeacherInfoCell({ absence, date, rowBg, rowHeight, onOpenDetail, isAutoAssigning, onStatsUpdate, onAssignmentsLoaded }: TeacherInfoCellProps) {
  const { data: assignments = [] } = useQuery({
    queryKey: queryKeys.assignments.list({ absence_id: absence.id, date }),
    queryFn: () => getDraftAssignments(absence.id, date),
    enabled: !!absence.id && !!date,
  });

  useEffect(() => {
    onAssignmentsLoaded(absence.id, assignments);
    const pending = assignments.filter((a) => a.substitute_teacher_id === null).length;
    const covered = assignments.filter((a) => a.substitute_teacher_id !== null && a.status === 'draft').length;
    const posted = assignments.filter((a) => a.status === 'posted').length;
    onStatsUpdate(absence.id, pending, covered, posted);
  }, [assignments, absence.id, onAssignmentsLoaded, onStatsUpdate]);

  const dayOfWeek = useMemo(() => {
    const days = ['SUNDAY','MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY'];
    const [y, m, d] = date.split('-').map(Number);
    return days[new Date(y, m - 1, d).getDay()];
  }, [date]);

  const { data: timetableRecords = [] } = useQuery({
    queryKey: ['teacher-timetable', absence.teacher_id, dayOfWeek],
    queryFn: () => getByTeacherDirect(absence.teacher_id),
    enabled: !!absence.teacher_id,
    select: (records) => records.filter((r: any) => r.day_of_week === dayOfWeek),
  });

  const primarySubjectClass = useMemo(() => {
    const first = (timetableRecords as any[])[0];
    if (!first) return null;
    const subj = first.subject?.subject_name;
    const cls = first.class_section?.class?.class_name;
    const sec = first.class_section?.section;
    if (subj && cls) return `${subj} · ${cls}${sec || ''}`;
    return null;
  }, [timetableRecords]);

  const total = assignments.length;
  const assigned = assignments.filter((a) => a.substitute_teacher_id !== null).length;
  const posted = assignments.filter((a) => a.status === 'posted').length;
  const allPosted = total > 0 && posted === total;
  const progress = total > 0 ? (assigned / total) * 100 : 0;
  const initials = absence.teacher.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <Box
      sx={{
        bgcolor: rowBg, px: 1.5,
        display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.75,
        cursor: 'pointer',
        height: rowHeight, // fixed height — syncs with middle and right panels
        borderBottom: '1px solid', borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
        '&:hover .teacher-name': { color: '#0F766E' },
      }}
      onClick={() => onOpenDetail({
        absenceId: absence.id,
        teacherName: absence.teacher.name,
        absenceType: absence.absence_type,
        absenceStatus: absence.status,
      })}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 600, bgcolor: allPosted ? '#166534' : '#0F766E', color: '#fff' }}>
          {isAutoAssigning ? <CircularProgress size={14} color="inherit" /> : initials}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography className="teacher-name" sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, transition: 'color 0.15s' }} noWrap>
            {absence.teacher.name}
          </Typography>
          <Typography sx={{ fontSize: 10, color: 'text.secondary', lineHeight: 1.3 }} noWrap>
            {primarySubjectClass || (absence.absence_type === 'full_day' ? 'Full Day' : 'Temp Duty')}
          </Typography>
        </Box>
      </Box>
      {total > 0 && (
        <Box>
          <LinearProgress
            variant={isAutoAssigning ? 'indeterminate' : 'determinate'}
            value={progress}
            sx={{
              height: 3, borderRadius: 2, mb: 0.25,
              bgcolor: 'rgba(0,0,0,0.08)',
              '& .MuiLinearProgress-bar': { bgcolor: allPosted ? '#166534' : '#0F766E' },
            }}
          />
          <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{assigned}/{total} covered</Typography>
        </Box>
      )}
    </Box>
  );
}

// ─── PeriodCellRow — middle scrollable row ────────────────────────────────────

interface PeriodCellRowProps {
  absence: AbsenceWithStatus;
  date: string;
  periods: SchedulePeriod[];
  absentTeacherIds: Set<string>;
  isAdmin: boolean;
  rowBg: string;
  rowHeight: number;
  onCellClick: (periodId: string, assignment?: DraftAssignment) => void;
  onCellClickUnassigned: (periodId: string, info: TimetablePeriodInfo, absence: AbsenceWithStatus) => void;
  onScheduledPeriodsLoaded: (absenceId: string, scheduledPeriodIds: Set<string>) => void;
  /** Set of assignment IDs that have a cross-row scheduling conflict */
  schedulingConflictIds: Set<string>;
}

function PeriodCellRow({ absence, date, periods, absentTeacherIds, isAdmin, rowBg, rowHeight, onCellClick, onCellClickUnassigned, onScheduledPeriodsLoaded, schedulingConflictIds }: PeriodCellRowProps) {
  const { data: assignments = [], isLoading } = useQuery({
    queryKey: queryKeys.assignments.list({ absence_id: absence.id, date }),
    queryFn: () => getDraftAssignments(absence.id, date),
    enabled: !!absence.id && !!date,
  });

  const dayOfWeek = useMemo(() => {
    const days = ['SUNDAY','MONDAY','TUESDAY','WEDNESDAY','THURSDAY','FRIDAY','SATURDAY'];
    const [y, m, d] = date.split('-').map(Number);
    return days[new Date(y, m - 1, d).getDay()];
  }, [date]);

  const { data: timetableRecords = [] } = useQuery({
    queryKey: ['teacher-timetable', absence.teacher_id, dayOfWeek],
    queryFn: () => getByTeacherDirect(absence.teacher_id),
    enabled: !!absence.teacher_id,
    select: (records) => records.filter((r: any) => r.day_of_week === dayOfWeek),
  });

  const scheduledPeriodIds = useMemo(
    () => new Set((timetableRecords as any[]).map((r: any) => r.period_id)),
    [timetableRecords]
  );

  // Map period_id → timetable info (class, subject) for Case 2 cells
  const byPeriodTimetable = useMemo(() => {
    const map = new Map<string, TimetablePeriodInfo>();
    for (const r of timetableRecords as any[]) {
      if (!r.period_id) continue;
      map.set(r.period_id, {
        classSectionId: r.class_section_id ?? r.class_section?.id ?? '',
        subjectId: r.subject_id ?? r.subject?.id ?? '',
        className: r.class_section?.class?.class_name ?? '—',
        section: r.class_section?.section ?? '',
        subjectName: r.subject?.subject_name ?? '—',
      });
    }
    return map;
  }, [timetableRecords]);

  useEffect(() => {
    onScheduledPeriodsLoaded(absence.id, scheduledPeriodIds);
  }, [scheduledPeriodIds, absence.id, onScheduledPeriodsLoaded]);

  const byPeriod = new Map(assignments.map((a) => [a.period_id, a]));

  return (
    <Box sx={{
      display: 'flex', gap: 1, px: 1, py: 1,
      bgcolor: rowBg, minWidth: 'fit-content',
      borderBottom: '1px solid', borderColor: 'divider',
      '&:last-child': { borderBottom: 'none' },
      alignItems: 'stretch',
      height: rowHeight,
      boxSizing: 'border-box',
    }}>
      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: 200 }}>
          <CircularProgress size={14} />
          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Loading…</Typography>
        </Box>
      ) : periods.map((period) => (
        <GridCell
          key={period.id}
          assignment={byPeriod.get(period.id)}
          period={period}
          needsSubstitution={scheduledPeriodIds.has(period.id) && !byPeriod.has(period.id)}
          timetableInfo={byPeriodTimetable.get(period.id)}
          absentTeacherIds={absentTeacherIds}
          isSchedulingConflict={!!byPeriod.get(period.id)?.id && schedulingConflictIds.has(byPeriod.get(period.id)!.id)}
          isAdmin={isAdmin}
          onClick={onCellClick}
          onClickUnassigned={(periodId, info) => onCellClickUnassigned(periodId, info, absence)}
        />
      ))}
    </Box>
  );
}

// ─── ActionCell — right frozen panel cell ────────────────────────────────────

interface ActionCellProps {
  absence: AbsenceWithStatus;
  rowBg: string;
  rowHeight: number;
  onAutoAssign: (absenceId: string) => void;
  onDiscard: (absenceId: string) => void;
  onRecall: (absenceId: string) => void;
  isAutoAssigning: boolean;
  isDiscarding: boolean;
  isRecalling: boolean;
  rowAssignments: DraftAssignment[];
}

function ActionCell({ absence, rowBg, rowHeight, onAutoAssign, onDiscard, onRecall, isAutoAssigning, isDiscarding, isRecalling, rowAssignments }: ActionCellProps) {
  const total = rowAssignments.length;
  const posted = rowAssignments.filter((a) => a.status === 'posted').length;
  const allPosted = total > 0 && posted === total;

  return (
    <Box sx={{
      bgcolor: rowBg,
      height: rowHeight, // fixed height — syncs with left and middle panels
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
      borderBottom: '1px solid', borderColor: 'divider',
      '&:last-child': { borderBottom: 'none' },
      px: 0.75,
    }}>
      {allPosted ? (
        <Tooltip title="Recall and re-draft">
          <span>
            <IconButton size="small" color="warning" disabled={isRecalling}
              onClick={() => onRecall(absence.id)}
              sx={{ border: '1px solid', borderColor: 'warning.main', borderRadius: 1.5 }}>
              {isRecalling ? <CircularProgress size={14} /> : <UndoIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <>
          <Tooltip title="Auto-assign">
            <span>
              <IconButton size="small" disabled={isAutoAssigning || isDiscarding}
                onClick={() => onAutoAssign(absence.id)}
                sx={{ border: '1px solid', borderColor: '#0F766E', borderRadius: 1.5, color: '#0F766E' }}>
                {isAutoAssigning ? <CircularProgress size={14} /> : <FlashOnIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Discard drafts">
            <span>
              <IconButton size="small" color="error" disabled={isAutoAssigning || isDiscarding}
                onClick={() => onDiscard(absence.id)}
                sx={{ border: '1px solid', borderColor: 'error.main', borderRadius: 1.5 }}>
                {isDiscarding ? <CircularProgress size={14} /> : <DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}
    </Box>
  );
}

// ─── Main Grid ────────────────────────────────────────────────────────────────

export function SubstitutionGrid({ date, absences, absentTeacherIds, onStatsChange }: SubstitutionGridProps) {
  const queryClient = useQueryClient();
  const { user, schoolContext } = useAuthStore();
  const { success: showSuccess, error: showError } = useToast();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [assignModal, setAssignModal] = useState<AssignModalState | null>(null);
  const [newAssignModal, setNewAssignModal] = useState<UnassignedPeriodContext | null>(null);
  const [drawerState, setDrawerState] = useState<DrawerState | null>(null);
  const [confirmState, setConfirmState] = useState<ConfirmState>({ open: false, title: '', message: '', onConfirm: () => {} });
  const [autoAssigningRows, setAutoAssigningRows] = useState<Set<string>>(new Set());
  const [discardingRows, setDiscardingRows] = useState<Set<string>>(new Set());
  const [recallingRows, setRecallingRows] = useState<Set<string>>(new Set());

  // Progress dialog state — per-teacher auto-assign
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [progressDialogRunning, setProgressDialogRunning] = useState(false);
  const [currentProgress, setCurrentProgress] = useState<AutoAssignProgress | null>(null);
  // Ref to track dialog open state without stale closure issues
  const progressDialogOpenRef = useRef(false);

  // Per-row assignments for footer computation
  const [rowAssignments, setRowAssignments] = useState<Map<string, DraftAssignment[]>>(new Map());

  // Per-row scheduled period IDs (periods where teacher has a class, assigned or not)
  const [rowScheduledPeriods, setRowScheduledPeriods] = useState<Map<string, Set<string>>>(new Map());

  // Per-row stats for parent chip counts
  const [rowStats, setRowStats] = useState<Map<string, { pending: number; covered: number; published: number }>>(new Map());

  const handleAssignmentsLoaded = useCallback((absenceId: string, assignments: DraftAssignment[]) => {
    setRowAssignments((prev) => {
      const next = new Map(prev);
      next.set(absenceId, assignments);
      return next;
    });
  }, []);

  const handleScheduledPeriodsLoaded = useCallback((absenceId: string, scheduledPeriodIds: Set<string>) => {
    setRowScheduledPeriods((prev) => {
      const next = new Map(prev);
      next.set(absenceId, scheduledPeriodIds);
      return next;
    });
  }, []);

  // Per-period coverage counts for footer
  // total = number of absent teachers who have a class in that period (assigned or not)
  // covered = number of those who have a substitute assigned
  const periodCoverage = useMemo(() => {
    const map = new Map<string, { covered: number; total: number }>();

    // Count all scheduled periods (needs sub or already assigned)
    rowScheduledPeriods.forEach((scheduledPeriodIds) => {
      scheduledPeriodIds.forEach((periodId) => {
        const cur = map.get(periodId) ?? { covered: 0, total: 0 };
        cur.total++;
        map.set(periodId, cur);
      });
    });

    // Count covered (assigned) periods
    rowAssignments.forEach((assignments) => {
      assignments.forEach((a) => {
        if (a.substitute_teacher_id !== null) {
          const cur = map.get(a.period_id);
          if (cur) {
            cur.covered++;
          } else {
            // Assignment exists but wasn't in scheduled periods — count it anyway
            map.set(a.period_id, { covered: 1, total: 1 });
          }
        }
      });
    });

    return map;
  }, [rowAssignments, rowScheduledPeriods]);

  // Detect cross-row scheduling conflicts:
  // A conflict exists when the same substitute teacher is assigned to two different
  // absences whose periods overlap in time on the same date.
  // Uses time-overlap (not period_id equality) so cross-schedule conflicts are caught.
  const schedulingConflictIds = useMemo(() => {
    // Collect all assignments with their time ranges: { id, teacherId, timeStart, timeEnd }
    type SlotEntry = { id: string; timeStart: string; timeEnd: string };
    const teacherSlots = new Map<string, SlotEntry[]>();

    rowAssignments.forEach((assignments) => {
      assignments.forEach((a) => {
        if (!a.substitute_teacher_id) return;
        const timeStart = a.period?.time_start;
        const timeEnd = a.period?.time_end;
        if (!timeStart || !timeEnd) return;
        const tid = a.substitute_teacher_id;
        if (!teacherSlots.has(tid)) teacherSlots.set(tid, []);
        teacherSlots.get(tid)!.push({ id: a.id, timeStart, timeEnd });
      });
    });

    // For each teacher, find all pairs of assignments that overlap in time
    const conflictIds = new Set<string>();
    teacherSlots.forEach((slots) => {
      for (let i = 0; i < slots.length; i++) {
        for (let j = i + 1; j < slots.length; j++) {
          const a = slots[i];
          const b = slots[j];
          // Overlap: a starts before b ends AND a ends after b starts
          if (a.timeStart < b.timeEnd && a.timeEnd > b.timeStart) {
            conflictIds.add(a.id);
            conflictIds.add(b.id);
          }
        }
      }
    });
    return conflictIds;
  }, [rowAssignments]);

  const handleStatsUpdate = useCallback((absenceId: string, pending: number, covered: number, published: number) => {
    setRowStats((prev) => {
      const next = new Map(prev);
      next.set(absenceId, { pending, covered, published });
      return next;
    });
  }, []);

  // Aggregate stats across all rows and notify parent
  useEffect(() => {
    if (!onStatsChange) return;
    let totalPending = 0, totalCovered = 0, totalPublished = 0;
    rowStats.forEach(({ pending, covered, published }) => {
      totalPending += pending;
      totalCovered += covered;
      totalPublished += published;
    });
    onStatsChange({ pending: totalPending, covered: totalCovered, published: totalPublished });
  }, [rowStats, onStatsChange]);

  // ── Fetch periods ──────────────────────────────────────────────────────────
  const { data: schedules = [], isLoading: loadingSchedules } = useQuery({
    queryKey: queryKeys.classSchedules.list({ school_id: schoolContext || '' }),
    queryFn: () => getClassSchedulesDirect(schoolContext || ''),
    enabled: !!schoolContext,
  });

  const periods: SchedulePeriod[] = schedules.length > 0
    ? [...(schedules[0].schedule_periods ?? [])].sort((a, b) => a.period_number - b.period_number)
    : [];

  // ── Mutations ──────────────────────────────────────────────────────────────

  const updateMutation = useMutation({
    mutationFn: ({ id, substituteTeacherId }: { id: string; substituteTeacherId: string }) =>
      updateAssignment(id, substituteTeacherId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
      showSuccess('Substitute assigned');
      setAssignModal(null);
    },
    onError: (err: any) => showError(err.message || 'Failed to assign'),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => removeAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
      showSuccess('Assignment removed');
    },
    onError: (err: any) => showError(err.message || 'Failed to remove'),
  });

  const createMutation = useMutation({
    mutationFn: (payload: Parameters<typeof createAssignment>[0]) => createAssignment(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
      showSuccess('Substitute assigned');
      setNewAssignModal(null);
    },
    onError: (err: any) => showError(err.message || 'Failed to assign'),
  });

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleAutoAssign = async (absenceId: string) => {
    setAutoAssigningRows((s) => new Set(s).add(absenceId));
    setCurrentProgress(null);
    setProgressDialogOpen(true);
    progressDialogOpenRef.current = true;
    setProgressDialogRunning(true);
    try {
      const result = await triggerEngine(absenceId, date, (progress) => {
        setCurrentProgress(progress);
      });

      // If a draft already exists, the engine skips — but there may be uncovered
      // periods (dashed cells). rerunAutoAssignment fills those gaps while
      // preserving any manually assigned periods.
      if (result.skipped_reason === 'draft_exists') {
        // Reset to null first — this clears the "Skipped" state in the dialog
        // and resets lastPhaseRef so the next phase transition fires cleanly.
        setCurrentProgress(null);
        await new Promise((r) => setTimeout(r, 50));
        setCurrentProgress({
          phase: 'matching',
          message: 'Filling remaining gaps…',
          absentTeacherName: '',
          periodsFound: 0,
          periodStates: [],
          periodsAssigned: 0,
          periodsUnassigned: 0,
        });
        const rerunResult = await rerunAutoAssignment(absenceId, date);
        setCurrentProgress({
          phase: 'complete',
          message: rerunResult.periods_assigned > 0
            ? `${rerunResult.periods_assigned} gap${rerunResult.periods_assigned !== 1 ? 's' : ''} filled`
            : 'Done — no additional teachers available',
          absentTeacherName: '',
          periodsFound: rerunResult.periods_affected,
          periodStates: [],
          periodsAssigned: rerunResult.periods_assigned,
          periodsUnassigned: rerunResult.periods_unassigned,
          elapsedMs: 0,
        });
        // Wait for the dialog's MIN_PHASE_MS (600ms) transition to complete
        // before setProgressDialogRunning(false) fires in finally, so the
        // button shows "Done" rather than staying on "Cancel".
        // Need 2x MIN_PHASE_MS since matching→complete is two phase transitions.
        await new Promise((r) => setTimeout(r, 1400));
      }

      await queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
      await queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
    } catch (err: any) {
      if (!progressDialogOpenRef.current) {
        showError(err.message || 'Auto-assignment failed');
      }
    } finally {
      setProgressDialogRunning(false);
      setAutoAssigningRows((s) => { const n = new Set(s); n.delete(absenceId); return n; });
    }
  };

  const handleDiscard = (absenceId: string) => {
    setConfirmState({
      open: true,
      title: 'Discard Drafts',
      message: 'Discard all draft assignments for this teacher on this date?',
      onConfirm: async () => {
        setConfirmState((s) => ({ ...s, open: false }));
        setDiscardingRows((s) => new Set(s).add(absenceId));
        try {
          await discardDraft(absenceId, date);
          queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
          queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
          showSuccess('Drafts discarded');
        } catch (err: any) {
          showError(err.message || 'Failed to discard');
        } finally {
          setDiscardingRows((s) => { const n = new Set(s); n.delete(absenceId); return n; });
        }
      },
    });
  };

  const handleRecall = (absenceId: string) => {
    setConfirmState({
      open: true,
      title: 'Recall and Re-draft',
      message: 'This will revert all published assignments back to draft and send cancellation notifications to assigned substitutes. Continue?',
      onConfirm: async () => {
        setConfirmState((s) => ({ ...s, open: false }));
        setRecallingRows((s) => new Set(s).add(absenceId));
        try {
          await recallAndRedraft(absenceId, date);
          queryClient.invalidateQueries({ queryKey: queryKeys.assignments.all });
          queryClient.invalidateQueries({ queryKey: queryKeys.absences.all });
          showSuccess('Plan recalled — reverted to draft');
        } catch (err: any) {
          showError(err.message || 'Failed to recall');
        } finally {
          setRecallingRows((s) => { const n = new Set(s); n.delete(absenceId); return n; });
        }
      },
    });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  // Sync middle header scroll with middle body scroll
  // IMPORTANT: These hooks must be called before any conditional returns
  const middleScrollRef = useRef<HTMLDivElement>(null);
  const middleHeaderRef = useRef<HTMLDivElement>(null);

  const handleMiddleScroll = useCallback(() => {
    if (middleScrollRef.current && middleHeaderRef.current) {
      middleHeaderRef.current.scrollLeft = middleScrollRef.current.scrollLeft;
    }
  }, []);

  if (absences.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 1 }} />
        <Typography variant="h6" color="text.secondary">No absences on this date</Typography>
        <Typography variant="body2" color="text.disabled">All teachers are present.</Typography>
      </Box>
    );
  }

  // Row background alternation
  const ROW_BG_ODD = 'background.paper';
  const ROW_BG_EVEN = 'action.hover';
  const ROW_H = 88;
  const HEADER_H = 56;
  const FOOTER_H = 36;

  const TEACHER_COL_W = 180;
  const ACTIONS_COL_W = isAdmin ? 80 : 0;

  return (
    <Box sx={{
      position: 'relative',
      border: '1px solid', borderColor: 'divider', borderRadius: 1,
      maxHeight: 'calc(100vh - 180px)', // leave room for toolbar
      overflowY: 'auto',
      overscrollBehaviorY: 'contain',
    }}>

      {/* ══ HEADER ROW — fully static, no scroll participation ══ */}
      <Box sx={{
        display: 'flex', alignItems: 'stretch',
        position: 'sticky', top: 0, zIndex: 10,
        bgcolor: '#ECE6F0',
        borderBottom: '1px solid #CAC4D0',
      }}>
        {/* Left header — Absent Teacher */}
        <Box sx={{
          width: TEACHER_COL_W, flexShrink: 0,
          display: 'flex', alignItems: 'center',
          px: 1.5, height: HEADER_H,
          borderRight: '1px solid #CAC4D0',
        }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#49454F', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Absent Teacher
          </Typography>
        </Box>

        {/* Middle header — period columns, clips to visible area, synced via JS */}
        <Box
          ref={middleHeaderRef}
          sx={{
            flex: 1,
            overflow: 'hidden', // no scrollbar, no bounce — purely clipped
            display: 'flex',
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, px: 1, alignItems: 'center', flexShrink: 0 }}>
            {loadingSchedules ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={12} sx={{ color: '#49454F' }} />
                <Typography sx={{ fontSize: 11, color: '#49454F' }}>Loading…</Typography>
              </Box>
            ) : periods.map((p) => {
              const cov = periodCoverage.get(p.id);
              const scheduledCount = cov?.total ?? 0;
              const freeCount = absences.length - scheduledCount;

              // Build list of free teachers for this period (absent but no class here)
              const freeTeachers = freeCount > 0
                ? absences.filter((abs) => {
                    const scheduled = rowScheduledPeriods.get(abs.id);
                    return !scheduled || !scheduled.has(p.id);
                  })
                : [];

              const freeTooltipContent = freeTeachers.length > 0 ? (
                <Box sx={{ p: 0.5, minWidth: 160 }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#fff', lineHeight: 1.4, mb: 0.75 }}>
                    Free — P{p.period_number}
                  </Typography>
                  {freeTeachers.map((abs) => {
                    const initials = abs.teacher.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();
                    return (
                      <Box key={abs.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Box sx={{
                          width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                          bgcolor: '#6750A4', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                            {initials}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.3 }}>
                            {abs.teacher.name}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              ) : null;

              return (
                <Box key={p.id} sx={{ width: 110, flexShrink: 0, textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#1C1B1F', lineHeight: 1.3 }}>
                    P {p.period_number}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: '#49454F', lineHeight: 1.3 }}>
                    {p.time_start.slice(0, 5)}–{p.time_end.slice(0, 5)}
                  </Typography>
                  {freeCount > 0 && (
                    <Tooltip
                      title={freeTooltipContent}
                      placement="bottom"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: '#313033',
                            maxWidth: 220,
                            '& .MuiTooltip-arrow': { color: '#313033' },
                          },
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          bgcolor: '#E8DEF8', borderRadius: 10, px: 0.75, py: 0.25, mt: 0.25,
                          cursor: 'default',
                        }}
                      >
                        <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#4F378B', lineHeight: 1.3 }}>
                          {freeCount} FREE
                        </Typography>
                      </Box>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Right header — Actions */}
        {isAdmin && (
          <Box sx={{
            width: ACTIONS_COL_W, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: HEADER_H,
            borderLeft: '1px solid #CAC4D0',
          }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#49454F', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Actions
            </Typography>
          </Box>
        )}
      </Box>

      {/* ══ BODY: left frozen | middle scroll | right frozen ══ */}
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>

        {/* ── LEFT FROZEN PANEL (no header here — header is above) ── */}
        <Box sx={{ width: TEACHER_COL_W, flexShrink: 0, zIndex: 2, borderRight: '1px solid', borderColor: 'divider' }}>
          {absences.map((absence, idx) => {
            const rowBg = idx % 2 === 0 ? ROW_BG_ODD : ROW_BG_EVEN;
            return (
              <TeacherInfoCell
                key={absence.id}
                absence={absence}
                date={date}
                rowBg={rowBg}
                rowHeight={ROW_H}
                onOpenDetail={setDrawerState}
                isAutoAssigning={autoAssigningRows.has(absence.id)}
                onStatsUpdate={handleStatsUpdate}
                onAssignmentsLoaded={handleAssignmentsLoaded}
              />
            );
          })}
          {/* Footer cell — sticky at bottom */}
          <Box sx={{
            bgcolor: 'action.hover', px: 1.5,
            borderTop: '2px solid', borderRight: '1px solid', borderColor: 'divider',
            height: FOOTER_H, display: 'flex', alignItems: 'center',
            position: 'sticky', bottom: 0, zIndex: 3,
          }}>
            <Typography sx={{ fontSize: 10, color: 'text.secondary', fontWeight: 600 }}>Coverage</Typography>
          </Box>
        </Box>

        {/* ── MIDDLE SCROLL CONTAINER (no header here — header is above) ── */}
        <Box
          ref={middleScrollRef}
          onScroll={handleMiddleScroll}
          sx={{ flex: 1, overflowX: 'auto', overscrollBehaviorX: 'contain' }}
        >
          {/* Period cell rows */}
          {absences.map((absence, idx) => {
            const rowBg = idx % 2 === 0 ? ROW_BG_ODD : ROW_BG_EVEN;
            return (
              <PeriodCellRow
                key={absence.id}
                absence={absence}
                date={date}
                periods={periods}
                absentTeacherIds={absentTeacherIds}
                isAdmin={isAdmin}
                rowBg={rowBg}
                rowHeight={ROW_H}
                schedulingConflictIds={schedulingConflictIds}
                onScheduledPeriodsLoaded={handleScheduledPeriodsLoaded}
                onCellClick={(periodId, assignment) => {
                  if (assignment) {
                    // Determine conflict context for the modal
                    const subIsAbsent = !!assignment.substitute_teacher_id && absentTeacherIds.has(assignment.substitute_teacher_id);
                    const subIsInactive = assignment.substitute_teacher != null && !assignment.substitute_teacher.is_active;
                    const isSchedConflict = schedulingConflictIds.has(assignment.id);
                    const isUnassigned = assignment.substitute_teacher_id === null;
                    const cantAutoAssign = isUnassigned && assignment.unassigned_reason === 'no_eligible_teachers';

                    let conflictInfo: AssignModalState['conflictInfo'];
                    if (isSchedConflict) {
                      conflictInfo = {
                        type: 'scheduling',
                        label: `${assignment.substitute_teacher?.name ?? 'This teacher'} is already assigned to another class at this time.`,
                      };
                    } else if (subIsAbsent) {
                      conflictInfo = {
                        type: 'absent',
                        label: `${assignment.substitute_teacher?.name ?? 'This teacher'} is marked absent today.`,
                      };
                    } else if (subIsInactive) {
                      conflictInfo = {
                        type: 'inactive',
                        label: `${assignment.substitute_teacher?.name ?? 'This teacher'} is currently inactive.`,
                      };
                    }

                    setAssignModal({
                      assignment,
                      conflictInfo,
                      cantAutoAssign,
                      onRemove: assignment.status === 'draft'
                        ? () => removeMutation.mutate(assignment.id)
                        : undefined,
                    });
                  }
                  // Case 2 (no assignment) is handled by onCellClickUnassigned below
                }}
                onCellClickUnassigned={(periodId, info, abs) => {
                  const period = periods.find((p) => p.id === periodId);
                  if (!period || !schoolContext) return;
                  setNewAssignModal({
                    absenceId: abs.id,
                    absentTeacherId: abs.teacher_id,
                    schoolId: schoolContext,
                    date,
                    periodId,
                    classSectionId: info.classSectionId,
                    subjectId: info.subjectId,
                    className: info.className,
                    subjectName: info.subjectName,
                    periodNumber: period.period_number,
                    timeStart: period.time_start,
                    timeEnd: period.time_end,
                  });
                }}
              />
            );
          })}

          {/* Footer coverage row — sticky at bottom */}
          {periods.length > 0 && (
            <Box sx={{
              display: 'flex', gap: 1, px: 1,
              minWidth: 'fit-content',
              borderTop: '2px solid', borderColor: 'divider',
              bgcolor: 'action.hover',
              height: FOOTER_H, alignItems: 'center',
              position: 'sticky', bottom: 0, zIndex: 3,
            }}>
              {periods.map((p) => {
                const cov = periodCoverage.get(p.id) ?? { covered: 0, total: 0 };
                const allCovered = cov.total > 0 && cov.covered === cov.total;
                return (
                  <Box key={p.id} sx={{ width: 110, flexShrink: 0, textAlign: 'center' }}>
                    <Typography sx={{
                      fontSize: 11, fontWeight: 600,
                      color: allCovered ? '#166534' : cov.covered > 0 ? '#0F766E' : 'text.secondary',
                    }}>
                      {cov.total > 0 ? `${cov.covered}/${cov.total}` : '—'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>

        {/* ── RIGHT FROZEN PANEL (no header here — header is above) ── */}
        {isAdmin && (
          <Box sx={{ width: ACTIONS_COL_W, flexShrink: 0, zIndex: 2, borderLeft: '1px solid', borderColor: 'divider' }}>
            {absences.map((absence, idx) => {
              const rowBg = idx % 2 === 0 ? ROW_BG_ODD : ROW_BG_EVEN;
              return (
                <ActionCell
                  key={absence.id}
                  absence={absence}
                  rowBg={rowBg}
                  rowHeight={ROW_H}
                  onAutoAssign={handleAutoAssign}
                  onDiscard={handleDiscard}
                  onRecall={handleRecall}
                  isAutoAssigning={autoAssigningRows.has(absence.id)}
                  isDiscarding={discardingRows.has(absence.id)}
                  isRecalling={recallingRows.has(absence.id)}
                  rowAssignments={rowAssignments.get(absence.id) ?? []}
                />
              );
            })}
            {/* Footer spacer — sticky at bottom */}
            <Box sx={{
              bgcolor: 'action.hover', height: FOOTER_H,
              borderTop: '2px solid', borderLeft: '1px solid', borderColor: 'divider',
              position: 'sticky', bottom: 0, zIndex: 3,
            }} />
          </Box>
        )}
      </Box>

      {/* ── Assign Modal (existing assignment) ── */}
      {assignModal && (
        <AssignModal
          state={assignModal}
          absentTeacherIds={absentTeacherIds}
          onClose={() => setAssignModal(null)}
          onAssign={(teacherId) => updateMutation.mutate({ id: assignModal.assignment.id, substituteTeacherId: teacherId })}
          isPending={updateMutation.isPending}
        />
      )}

      {/* ── New Assign Modal (Case 2 — no DB record yet) ── */}
      {newAssignModal && (
        <NewAssignModal
          ctx={newAssignModal}
          absentTeacherIds={absentTeacherIds}
          onClose={() => setNewAssignModal(null)}
          onAssign={(teacherId) => createMutation.mutate({
            school_id: newAssignModal.schoolId,
            absence_id: newAssignModal.absenceId,
            date: newAssignModal.date,
            class_section_id: newAssignModal.classSectionId,
            period_id: newAssignModal.periodId,
            subject_id: newAssignModal.subjectId,
            absent_teacher_id: newAssignModal.absentTeacherId,
            substitute_teacher_id: teacherId,
          })}
          isPending={createMutation.isPending}
        />
      )}

      {/* ── Detail drawer (DraftReviewPanel — audit log, recall, re-draft) ── */}
      <Drawer
        anchor="right"
        open={!!drawerState}
        onClose={() => setDrawerState(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 520 }, p: 3 } }}
      >
        {drawerState && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" fontWeight="bold">{drawerState.teacherName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {drawerState.absenceType === 'full_day' ? 'Full Day Absence' : 'Temporary Duty'}
                </Typography>
              </Box>
              <IconButton onClick={() => setDrawerState(null)}><CloseIcon /></IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <DraftReviewPanel
              absenceId={drawerState.absenceId}
              targetDate={date}
              absentTeacherName={drawerState.teacherName}
              absenceType={drawerState.absenceType}
              absenceStatus={drawerState.absenceStatus}
              onClose={() => setDrawerState(null)}
            />
          </Box>
        )}
      </Drawer>

      {/* ── Confirm dialog ── */}
      <ConfirmDialog
        open={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        severity="warning"
        onConfirm={confirmState.onConfirm}
        onCancel={() => setConfirmState((s) => ({ ...s, open: false }))}
      />

      {/* ── Auto-assign progress dialog (per-teacher) ── */}
      <AutoAssignProgressDialog
        open={progressDialogOpen}
        progress={currentProgress}
        isRunning={progressDialogRunning}
        onClose={() => {
          setProgressDialogOpen(false);
          progressDialogOpenRef.current = false;
          setCurrentProgress(null);
        }}
      />
    </Box>
  );
}
